import React from 'react';
import { Users, UserCheck, Briefcase, Building2 } from 'lucide-react';

/**
 * TeamInfoBanner - Shows context for recruiters and account managers
 * - For Recruiters: Displays team name, lead recruiter, and team's assigned clients
 * - For Account Managers: Displays their assigned clients
 */
const TeamInfoBanner = ({ selectedUser, teamLead, myTeam }) => {
  if (!selectedUser) return null;
  
  const isAccountManager = selectedUser.role === 'account_manager' || 
                          selectedUser.role === 'account_exec' ||
                          selectedUser.roles?.includes('account_manager');
  
  const isRecruiter = selectedUser.role === 'recruiter';
  
  // Show for Account Managers (their assigned clients)
  if (isAccountManager) {
    const assignedClients = selectedUser.assigned_clients || [];
    
    return (
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3">
          <Building2 className="h-5 w-5 text-orange-600 mt-0.5" />
          <div className="flex-1">
            <div className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-1">
              Your Assigned Clients
            </div>
            {assignedClients.length > 0 ? (
              <div className="text-sm text-gray-900">
                <span className="font-semibold">{assignedClients.length} clients</span>
                {assignedClients.length <= 5 ? (
                  <span className="text-gray-600 ml-2">
                    ({assignedClients.join(', ')})
                  </span>
                ) : (
                  <span className="text-gray-600 ml-2">
                    ({assignedClients.slice(0, 3).join(', ')}, +{assignedClients.length - 3} more)
                  </span>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-500">No clients assigned yet</div>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Show for recruiters who belong to a team
  if (isRecruiter && teamLead && myTeam) {
    // Get assigned clients from TEAM LEAD (single source of truth)
    const assignedClients = teamLead.assigned_clients || [];

    return (
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-6">
          {/* Team Info */}
          <div className="flex items-start gap-2 flex-1">
            <Users className="h-5 w-5 text-indigo-600 mt-0.5" />
            <div>
              <div className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Team</div>
              <div className="text-sm font-medium text-gray-900">{myTeam.team_name}</div>
            </div>
          </div>

          {/* Lead Recruiter */}
          <div className="flex items-start gap-2 flex-1">
            <UserCheck className="h-5 w-5 text-purple-600 mt-0.5" />
            <div>
              <div className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Lead Recruiter</div>
              <div className="text-sm font-medium text-gray-900">{teamLead.full_name}</div>
            </div>
          </div>

          {/* Assigned Clients */}
          <div className="flex items-start gap-2 flex-1">
            <Briefcase className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <div className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Lead Recruiter's Assigned Clients</div>
              {assignedClients.length > 0 ? (
                <div className="text-sm text-gray-900">
                  {assignedClients.length === 1 ? (
                    <span className="font-medium">{assignedClients[0]}</span>
                  ) : assignedClients.length <= 3 ? (
                    <span className="font-medium">{assignedClients.join(', ')}</span>
                  ) : (
                    <span className="font-medium">
                      {assignedClients.slice(0, 2).join(', ')}
                      <span className="text-gray-500"> +{assignedClients.length - 2} more</span>
                    </span>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-500">No clients assigned</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Don't show banner for other roles
  return null;
};

export default TeamInfoBanner;
